import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useUserPermissions } from '@/hooks/permissions';
import { PermissionKey } from '@/types/roles';

interface ProtectedRouteProps {
  children: ReactNode;
  /** Permission-based access control. If provided, user must have at least one of these permissions. */
  requiredPermissions?: PermissionKey[];
  /** If true, user must have ALL permissions (AND logic). Default is false (OR logic - any permission grants access). */
  requireAllPermissions?: boolean;
  redirectTo?: string;
}

const ProtectedRoute = ({ 
  children, 
  requiredPermissions = [],
  requireAllPermissions = false,
  redirectTo = '/access-denied' 
}: ProtectedRouteProps) => {
  const { profile, loading, user } = useAuth();
  const { permissions, isLoading: permissionsLoading } = useUserPermissions();

  // Show loading while auth state is being determined
  if (loading || loading === undefined) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // If no user, redirect to auth
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Wait for profile to load before checking access
  if (!profile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // PERMISSION-BASED ACCESS (new system - takes precedence)
  if (requiredPermissions.length > 0) {
    // Fail-closed: if permissions are still loading, show loading state
    if (permissionsLoading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
        </div>
      );
    }

    // Check permission access
    const hasAccess = requireAllPermissions
      ? requiredPermissions.every(perm => permissions.includes(perm))
      : requiredPermissions.some(perm => permissions.includes(perm));

    if (!hasAccess) {
      return <Navigate to={redirectTo} replace />;
    }
    
    // Permission check passed, render children
    return <>{children}</>;
  }

  // No requiredPermissions specified - allow all authenticated users
  return <>{children}</>;
};

export default ProtectedRoute;
